import { computed, ref, useContext, useRoute } from '@nuxtjs/composition-api';
import { normaliseResolvedStory } from '@shared/helpers/storyblok/normaliseResolvedStory';
import {
  IProductResult,
  ISearchResponse,
  searchGetters,
} from '@unified-commerce/gpc-vue-storefront-search';
import {
  getStoriesAsArray,
  getStoryBlokVersion,
  useContent,
} from '@unified-commerce/gpc-vue-storefront-storyblok';

import { SEARCH_BRAND_PREFIX_HANDLE, STORYBLOK_SETTINGS } from '~/constants';

import { getContentSearchSlugs } from './helpers/getContentSearchSlugs';
import { getSortedBrandCategoryHandlesByProductCount } from './helpers/getSortedBrandCategoryHandlesByProductCount';
import { sortPopularBrands } from './helpers/sortPopularBrands';

export interface SearchPopularBrandsProps {
  searchResult: ISearchResponse<IProductResult[]>;
  query?: string;
}

export interface UsePopularBrandsProps {
  numOfBrands?: number;
}

const usePopularBrands = (props: UsePopularBrandsProps = {}) => {
  const route = useRoute();
  const { $config } = useContext();
  const { search: searchCurated, content: curatedContent } = useContent('curated-popular-brands');
  const { search, content } = useContent('popular-brands');
  const version = getStoryBlokVersion($config.ENV, route.value);

  const { numOfBrands = 12 } = props;
  // an arbitrary 50% increase brands to fetch to allow for missing data in Storyblok.
  // this greatly increases our chances of presenting `numOfBrands` brands to user
  const brandFetchBuffer = 1.5;

  const sortedHandles = ref<string[]>([]);

  const curatedBrands = computed(() =>
    (curatedContent.value?.popular_brands ?? []).map(normaliseResolvedStory),
  );

  const brands = computed(() => {
    const popularBrandsAsArray = getStoriesAsArray(content.value);
    // Create a map of brand handle to its popularity rank (index) for sorting
    const sortedHandlesMap = new Map(sortedHandles.value.map((handle, index) => [handle, index]));
    return curatedBrands.value
      .concat(popularBrandsAsArray.sort(sortPopularBrands(sortedHandlesMap)))
      .slice(0, numOfBrands);
  });

  const getCuratedPopularBrands = async () => {
    await searchCurated({
      url: STORYBLOK_SETTINGS,
      relations: 'Settings.popular_brands',
      version,
    });
  };

  const resetCuratedPopularBrands = () => {
    if (curatedContent.value) {
      curatedContent.value.popular_brands = [];
    }
  };

  const fetchPopularBrands = async ({ searchResult, query }: SearchPopularBrandsProps) => {
    try {
      if (!query) {
        await getCuratedPopularBrands();
      } else {
        resetCuratedPopularBrands();
      }
    } catch (_) {
      // Continue on error
    }
    if (curatedBrands.value?.length >= 12) return;
    const categories = searchGetters.getCategories(searchResult) ?? {};
    sortedHandles.value = getSortedBrandCategoryHandlesByProductCount(
      categories,
      Math.round(numOfBrands * brandFetchBuffer),
      SEARCH_BRAND_PREFIX_HANDLE,
    );

    if (sortedHandles.value.length <= 0) {
      return;
    }
    await search({
      version,
      custom: {
        by_slugs: getContentSearchSlugs(sortedHandles.value),
      },
    });
  };

  return {
    brands,
    fetchPopularBrands,
  };
};

export default usePopularBrands;
